import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, Text } from '@chakra-ui/react';
import './App.css';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FirstUserLoginPage from './pages/FirstUserLoginPage';
import InfluencerSignUpWizard from './pages/InfluencerSignUpWizard';
import BusinessSignUpWizard from './pages/BusinessSignUpWizard';
import InfluencerHomePage from './pages/InfluencerHomePage';
import BusinessHomePage from './pages/BusinessHomePage';
import BusinessInfluencersListPage from './pages/BusinessInfluencersListPage';
import BusinessInfluencerDetailsPage from './pages/BusinessInfluencerDetailsPage';
import EventDetailsPage from './pages/EventDetailsPage';
import InfluencerEditProfilePage from './pages/InfluencerEditProfilePage';
import BusinessEditProfilePage from './pages/BusinessEditProfilePage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import BusinessEventCreationPage from './pages/BusinessEventCreationPage';
import AccountBlockedPage from './pages/AccountBlockedPage';
import SubscriptionExpiredPage from './pages/SubscriptionExpiredPage';
import AdminUsersListPage from './pages/AdminUsersListPage';
import UserProfileDetailsPage from './pages/UserProfileDetailsPage';
import { ConfigProvider, useConfig } from './contexts/ConfigContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { SupabaseProvider, useSupabase } from './contexts/SupabaseContext';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PaidPeriodGuard } from './components/PaidPeriodGuard';
import { ROUTES } from './constants';

function AppContent() {
  const { loading: configLoading, error: configError } = useConfig();
  const { user, loading: userLoading } = useUser();
  const { isLoading: supabaseLoading } = useSupabase();

  if (userLoading || configLoading || supabaseLoading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="xl" color="teal.500">Loading...</Text>
      </Box>
    );
  }
  if (configError) {
    return <Text color="red.500">Failed to load configuration: {configError}</Text>;
  }

  return (
    <Box 
      minH="100vh" 
      bg="#120F1B"
      border="2px solid #C6D959"
      position="relative"
    >
      <Routes>
        <Route path={ROUTES.HOME} element={<LandingPage user={user} />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.FIRST_USER_LOGIN} element={<FirstUserLoginPage />} />
        <Route path={ROUTES.INFLUENCER_SIGNUP_WIZARD} element={<InfluencerSignUpWizard />} />
        <Route path={ROUTES.BUSINESS_SIGNUP_WIZARD} element={<BusinessSignUpWizard />} />
        <Route path={ROUTES.INFLUENCER_HOME} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <InfluencerHomePage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_HOME} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessHomePage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_INFLUENCERS} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessInfluencersListPage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_INFLUENCER_DETAILS} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessInfluencerDetailsPage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.EVENT_DETAILS} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <EventDetailsPage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.EDIT_PROFILE} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <InfluencerEditProfilePage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_EDIT_PROFILE} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessEditProfilePage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_PROFILE} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessProfilePage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.BUSINESS_EVENT_CREATION} element={
          <PaidPeriodGuard>
            <ProtectedRoute>
              <BusinessEventCreationPage />
            </ProtectedRoute>
          </PaidPeriodGuard>
        } />
        <Route path={ROUTES.ACCOUNT_BLOCKED} element={<AccountBlockedPage />} />
        <Route path={ROUTES.SUBSCRIPTION_EXPIRED} element={<SubscriptionExpiredPage />} />
        
        {/* Admin Routes */}
        <Route path={ROUTES.ADMIN_USERS} element={
          <ProtectedAdminRoute>
            <AdminUsersListPage />
          </ProtectedAdminRoute>
        } />
        <Route path={ROUTES.ADMIN_USER_PROFILE} element={
          <ProtectedAdminRoute>
            <UserProfileDetailsPage />
          </ProtectedAdminRoute>
        } />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <ConfigProvider>
      <SupabaseProvider>
        <UserProvider>
          <AppContent />
        </UserProvider>
      </SupabaseProvider>
    </ConfigProvider>
  );
}

export default App;
